import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Tooltip } from '@/components/tooltip'
import { Table } from '@/components_deprecated/Table'
import { useAllIssueTags } from '@/modules/common/hooks/use-all-issue-tags'
import type { TagNode } from '@/modules/common/types'
import { useConfirm, useOnErrorAuto } from '@/utils/hooks'
import { useTagMutations } from '../../util/mutations.hooks'
import { OldAddButton, OldSection, SectionHeader } from '../common'
import type { IssueTagForm } from './issue-tag-prompt'
import { IssueTagPrompt } from './issue-tag-prompt'

interface Props {
  loading?: boolean
}

export const IssueTagSection: React.FC<Props> = ({ loading }) => {
  const translations = useTranslate({
    issueTag: 'settings.issue-tag',
    issueTags: 'settings.issue-tags',
    description: 'common.description',
    issues: 'common.issues',
    created: 'common.created',
    createdBy: ['common.created-by', { name: '' }],
    prompt: {
      deleteTag: ['settings.prompts.issue-tag-delete', { name: '' }],
      deleteTagTitle: 'settings.prompts.issue-tag-delete-title',
    },
  })

  const confirm = useConfirm()
  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const tagMutations = useTagMutations()

  const { tags } = useAllIssueTags({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onError: onErrorAuto(),
  })

  async function handleCreatePrompt() {
    const { data } = await addPrompt<IssueTagForm | null>(resolve => (
      <IssueTagPrompt
        existingTags={tags.map(t => ({ id: t.id, name: t.name }))}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    await tagMutations.create({
      variables: {
        input: {
          name: data.name,
          color: data.color,
          description: data.description,
          scope: 'ISSUE',
        },
      },
    })
  }

  async function handleEditPrompt(tag: TagNode) {
    const { data } = await addPrompt<IssueTagForm | null>(resolve => (
      <IssueTagPrompt
        initialData={{
          name: tag.name,
          color: tag.color,
          description: tag.description ?? '',
        }}
        tagId={tag.id}
        existingTags={tags.map(t => ({ id: t.id, name: t.name }))}
        onSubmit={resolve}
      />
    ))
    if (data === null) return

    await tagMutations.patch({
      variables: {
        id: tag.id,
        input: {
          name: data.name,
          color: data.color,
          description: data.description,
        },
      },
    })
  }

  async function handleDeletePrompt(tag: TagNode) {
    const { data: answer } = await confirm(
      translations.prompt.deleteTag({ name: tag.name }),
      translations.prompt.deleteTagTitle
    )
    if (!answer) return
    await tagMutations.delete({ variables: { id: tag.id } })
  }

  return (
    <OldSection>
      <header className="flex justify-between items-start">
        <SectionHeader loading={loading || tagMutations.loading}>
          {translations.issueTags}
        </SectionHeader>
        <OldAddButton adding={false} onClick={handleCreatePrompt} />
      </header>
      {tags.length > 0 && (
        <Table
          noShadow
          noBorders
          noHeaderMargin
          noRowMargin
          rowHoverColor="secondary"
        >
          <thead>
            <tr>
              <th>{translations.issueTag}</th>
              <th>{translations.description}</th>
              <th>{translations.issues}</th>
              <th>{translations.created}</th>
              <th colSpan={2} />
            </tr>
          </thead>
          <tbody>
            {tags.map(tag => (
              <tr key={tag.id}>
                <td>
                  <span
                    className="inline-block size-3 rounded-full mr-2 align-middle"
                    style={{ backgroundColor: tag.color }}
                  />
                  {tag.name}
                </td>
                <td>{tag.description}</td>
                <td>{tag.issuesCount}</td>
                <td>
                  {tag.createdAt ? (
                    tag.createdBy ? (
                      <Tooltip
                        asChild={false}
                        content={translations.createdBy({
                          name: tag.createdBy.fullName,
                        })}
                      >
                        <span className="cursor-help">
                          {new Date(tag.createdAt).toLocaleDateString()}
                        </span>
                      </Tooltip>
                    ) : (
                      new Date(tag.createdAt).toLocaleDateString()
                    )
                  ) : (
                    ''
                  )}
                </td>
                <td width="1px">
                  <Icon
                    icon="edit"
                    cursor="pointer"
                    color="gray6"
                    hoverColor="secondary"
                    onClick={() => handleEditPrompt(tag)}
                  />
                </td>
                <td width="1px">
                  <Icon
                    icon="times"
                    cursor="pointer"
                    color="gray6"
                    hoverColor="red"
                    onClick={() => handleDeletePrompt(tag)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </OldSection>
  )
}
